import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { useSettings } from "@/contexts/SettingsContext";
import { useTheme } from "@/contexts/ThemeContext";
import { TrendingUp, Wallet as WalletIcon, User as UserIcon, LogOut, ChevronDown, Globe, Shield, Menu, X, Gift, Trophy, Users, Sun, Moon, Bell, Check, Briefcase, Award } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel } from "@/components/ui/dropdown-menu";

export const AccountSwitcher = () => {
  const { user, switchMode } = useAuth();
  const { t } = useI18n();
  if (!user) return null;
  const isDemo = user.account_mode === "demo";
  const bal = isDemo ? user.demo_balance : user.real_balance;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="account-switcher"
          className="flex items-center gap-2 sm:gap-3 bg-panel border border-line px-2 sm:px-4 py-2 hover:bg-panel-hover transition-colors rounded-sm"
        >
          <span className={`w-2 h-2 rounded-full ${isDemo ? "bg-orange-400" : "bg-buy"}`} />
          <div className="text-left leading-tight">
            <div className="text-[10px] uppercase tracking-widest text-dim hidden sm:block">{isDemo ? t("demo_account") : t("real_account")}</div>
            <div className="font-mono text-xs sm:text-sm font-semibold" data-testid="account-balance">${Number(bal).toLocaleString("en-US", {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
          </div>
          <ChevronDown className="w-4 h-4 text-muted" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="bg-panel border-line text-white w-64" align="end">
        <DropdownMenuLabel className="text-dim text-[10px] tracking-widest uppercase">Switch Account</DropdownMenuLabel>
        <DropdownMenuItem data-testid="switch-demo" onClick={() => switchMode("demo")} className="flex justify-between focus:bg-panel-hover cursor-pointer">
          <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-orange-400" />{t("demo_account")}</div>
          <span className="font-mono text-sm">${Number(user.demo_balance).toFixed(2)}</span>
        </DropdownMenuItem>
        <DropdownMenuItem data-testid="switch-real" onClick={() => switchMode("real")} className="flex justify-between focus:bg-panel-hover cursor-pointer">
          <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-buy" />{t("real_account")}</div>
          <span className="font-mono text-sm">${Number(user.real_balance).toFixed(2)}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const LanguageSelector = () => {
  const { lang, setLang } = useI18n();
  const langs = [{c:"tr",n:"Türkçe"},{c:"en",n:"English"},{c:"es",n:"Español"},{c:"de",n:"Deutsch"}];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="language-selector" className="flex items-center gap-2 bg-panel border border-line px-2 sm:px-3 py-2 hover:bg-panel-hover rounded-sm text-sm text-muted">
          <Globe className="w-4 h-4" /> <span className="uppercase font-mono text-xs">{lang}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="bg-panel border-line text-white" align="end">
        {langs.map((l) => (
          <DropdownMenuItem key={l.c} onClick={() => setLang(l.c)} data-testid={`lang-${l.c}`} className="focus:bg-panel-hover cursor-pointer">
            <span className="font-mono uppercase text-xs w-8 text-dim">{l.c}</span>{l.n}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const ThemeToggle = () => {
  const { theme, toggleTheme } = useTheme();
  return (
    <button 
      onClick={toggleTheme} 
      className="p-2 text-muted hover:text-white hover:bg-panel-hover rounded-sm transition-colors"
      title="Temayı Değiştir"
    >
      {theme === "light" ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
    </button>
  );
};

export const NotificationsBell = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);

  const fetchNotifications = async () => {
    try {
      const { data } = await api.get("/account/notifications");
      setNotifications(Array.isArray(data) ? data : []);
    } catch (e) {
      setNotifications([]);
    }
  };

  useEffect(() => {
    if (user) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 60000); // Check every minute
      return () => clearInterval(interval);
    }
  }, [user]);

  if (!user) return null;

  const unreadCount = notifications.filter(n => !n.read).length;

  const markAsRead = async (id, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    try {
      await api.post(`/account/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    } catch (e) {}
  };

  const markAllAsRead = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    try {
      await api.post("/account/notifications/read-all");
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (e) {}
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="relative p-2 text-muted hover:text-white hover:bg-panel-hover rounded-sm transition-colors" title="Bildirimler">
          <Bell className="w-4 h-4" />
          {unreadCount > 0 && (
            <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-sell rounded-full shadow-[0_0_8px_rgba(239,68,68,0.8)]" />
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="bg-panel border-line text-white w-80 p-0 shadow-2xl" align="end">
        <div className="flex items-center justify-between px-3 py-2 border-b border-line bg-app">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-dim">Bildirimler {unreadCount > 0 && `(${unreadCount})`}</span>
          {unreadCount > 0 && (
            <button onClick={markAllAsRead} className="text-[9px] text-buy hover:underline uppercase tracking-widest flex items-center gap-1">
              <Check className="w-3 h-3" /> Tümünü Okundu İşaretle
            </button>
          )}
        </div>
        <div className="max-h-[70vh] overflow-y-auto" style={{ scrollbarWidth: "thin", scrollbarColor: "var(--iq-line) transparent" }}>
          {notifications.length === 0 ? (
            <div className="px-4 py-8 text-center text-dim text-xs">Henüz bildiriminiz yok.</div>
          ) : (
            notifications.map(n => (
              <div key={n.id} className={`px-4 py-3 border-b border-subtle last:border-b-0 hover:bg-panel-hover transition-colors ${n.read ? "opacity-60" : ""}`}>
                <div className="flex justify-between items-start mb-1 gap-2">
                  <span className={`text-xs font-semibold ${!n.read ? "text-buy" : "text-white"}`}>{n.title}</span>
                  {!n.read && (
                    <button onClick={(e) => markAsRead(n.id, e)} className="text-dim hover:text-white p-1 bg-line rounded-sm shrink-0" title="Okundu işaretle">
                      <Check className="w-3 h-3" />
                    </button>
                  )}
                </div>
                <div className="text-xs text-muted leading-relaxed mb-2 break-words">{n.message}</div>
                <div className="text-[9px] text-dim">{new Date(n.created_at).toLocaleString("tr-TR")}</div>
              </div>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default function TopBar() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const { settings, loading } = useSettings();
  const loc = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const link = (to, label, testid, Icon) => {
    const active = loc.pathname === to;
    return (
      <Link
        to={to}
        onClick={() => setOpen(false)}
        data-testid={testid}
        className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded-sm transition-colors ${active ? "bg-panel-hover text-white" : "text-muted hover:text-white hover:bg-panel-hover"}`}
      >
        <Icon className="w-4 h-4" /> {label}
      </Link>
    );
  };
  return (
    <header className="relative z-50 flex items-center justify-between px-3 sm:px-4 h-14 border-b border-line bg-panel"
      style={{ paddingTop: "var(--safe-top)" }}>
      <div className="flex items-center gap-6">
        <Link to={user ? "/trade" : "/"} className="flex items-center gap-2" data-testid="brand-logo">
          {loading ? (
            <div className="w-7 h-7 bg-transparent"></div>
          ) : settings?.logo_base64 ? (
            <img src={settings.logo_base64} alt="Logo" className="max-h-8" />
          ) : (
            <>
              <div className="w-7 h-7 bg-buy flex items-center justify-center rounded-sm">
                <TrendingUp className="w-4 h-4 text-black" />
              </div>
              <span className="font-bold tracking-tight text-lg">
                {settings?.site_title ? settings.site_title : (
                  <>IQ<span className="text-buy">Trade</span></>
                )}
              </span>
            </>
          )}
        </Link>
        {user && (
          <nav className="hidden md:flex items-center gap-1">
            {link("/trade", t("trade"), "nav-trade", TrendingUp)}
            {link("/portfolio", t("portfolio"), "nav-portfolio", Briefcase)}
            {link("/tournaments", t("tournaments"), "nav-tournaments", Award)}
            {link("/wallet", t("wallet"), "nav-wallet", WalletIcon)}
            {link("/leaderboard", t("leaderboard"), "nav-leaderboard", Trophy)}
            {link("/social", t("social"), "nav-social", Users)}
            {link("/referrals", t("referrals"), "nav-referrals", Gift)}
            {link("/profile", t("profile"), "nav-profile", UserIcon)}
            {user.role === "admin" && link("/admin", t("admin"), "nav-admin", Shield)}
          </nav>
        )}
      </div>
      <div className="flex items-center gap-1 sm:gap-3">
        <div className="hidden sm:block"><ThemeToggle /></div>
        <div className="hidden sm:block"><LanguageSelector /></div>
        {user ? (
          <>
            <NotificationsBell />
            <AccountSwitcher />
            <button data-testid="logout-btn" onClick={async () => { await logout(); nav("/"); }} className="hidden md:block p-2 text-muted hover:text-white hover:bg-panel-hover rounded-sm">
              <LogOut className="w-4 h-4" />
            </button>
            <button data-testid="mobile-menu" onClick={() => setOpen(!open)} className="md:hidden p-2 text-muted hover:text-white hover:bg-panel-hover rounded-sm">
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </>
        ) : (
          <>
            <Link to="/login" data-testid="topbar-login" className="text-sm text-muted hover:text-white px-2 sm:px-3 py-1.5">{t("login")}</Link>
            <Link to="/register" data-testid="topbar-register" className="text-xs sm:text-sm bg-buy text-black font-semibold px-3 sm:px-4 py-1.5 rounded-sm hover:bg-emerald-400">{t("register")}</Link>
          </>
        )}
      </div>
      {/* Mobile menu */}
      {open && user && (
        <div className="absolute top-14 left-0 right-0 z-40 bg-panel border-b border-line md:hidden">
          <nav className="flex flex-col p-2 gap-0.5 shadow-2xl">
            <div className="flex items-center justify-between px-3 py-2 border-b border-subtle mb-1 sm:hidden">
              <span className="text-xs text-dim uppercase">{t("preferences")}</span>
              <div className="flex items-center gap-2">
                <ThemeToggle />
                <LanguageSelector />
              </div>
            </div>
            {link("/trade", t("trade"), "mobile-nav-trade", TrendingUp)}
            {link("/portfolio", t("portfolio"), "mob-portfolio", Briefcase)}
            {link("/tournaments", t("tournaments"), "mob-tournaments", Award)}
            {link("/wallet", t("wallet"), "mob-wallet", WalletIcon)}
            {link("/leaderboard", t("leaderboard"), "mob-leaderboard", Trophy)}
            {link("/social", t("social"), "mob-social", Users)}
            {link("/referrals", t("referrals"), "mob-referrals", Gift)}
            {link("/profile", t("profile"), "mobile-nav-profile", UserIcon)}
            {user.role === "admin" && link("/admin", t("admin"), "mobile-nav-admin", Shield)}
            <button data-testid="mobile-logout" onClick={async () => { await logout(); nav("/"); }} className="flex items-center gap-2 px-3 py-1.5 text-sm text-muted hover:text-white hover:bg-panel-hover rounded-sm">
              <LogOut className="w-4 h-4" /> {t("logout")}
            </button>
          </nav>
        </div>
      )}
    </header>
  );
}
